import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, ContentCopy, DeleteOutline } from "@mui/icons-material";
import {
  AuthRequestError,
  createPersonalAccessToken,
  listPersonalAccessTokens,
  revokePersonalAccessToken,
} from "../../api/services/authService";
import type { CreatePersonalAccessTokenResult, PersonalAccessToken } from "../../types";
import { buildCursorMcpJson, getMcpEndpointUrl } from "../../utils/mcpConnectionConfig";

const tablePaperSx = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;

const DEFAULT_EXPIRY_DAYS = 90;
const MAX_EXPIRY_DAYS = 365;

const codeBlockSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  p: 1.5,
  borderRadius: 1,
  bgcolor: "action.hover",
  fontFamily: "monospace",
  fontSize: "0.8rem",
  wordBreak: "break-all" as const,
  whiteSpace: "pre-wrap" as const,
};

function CopyableBlock({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={codeBlockSx}>
        <Box component={multiline ? "pre" : "span"} sx={{ flex: 1, m: 0 }}>
          {value}
        </Box>
        <Tooltip title={copied ? "Copied" : "Copy"}>
          <IconButton size="small" onClick={handleCopy} aria-label={`Copy ${label}`}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function tokenStatus(token: PersonalAccessToken): { label: string; color: "default" | "success" | "warning" | "error" } {
  if (token.revoked) return { label: "Revoked", color: "error" };
  if (token.expired) return { label: "Expired", color: "warning" };
  return { label: "Active", color: "success" };
}

const PersonalAccessTokensPanel: React.FC = () => {
  const [tokens, setTokens] = useState<PersonalAccessToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createExpiryDays, setCreateExpiryDays] = useState(String(DEFAULT_EXPIRY_DAYS));
  const [creating, setCreating] = useState(false);

  const [createdToken, setCreatedToken] = useState<CreatePersonalAccessTokenResult | null>(null);
  const [copied, setCopied] = useState(false);

  const mcpEndpointUrl = getMcpEndpointUrl();
  const mcpConfigJson = buildCursorMcpJson();

  const loadTokens = useCallback(() => {
    setLoading(true);
    setError(null);
    listPersonalAccessTokens()
      .then(setTokens)
      .catch((err: unknown) => {
        const message =
          err instanceof AuthRequestError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed to load personal access tokens";
        setError(message);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) {
      setError("Token name is required");
      return;
    }
    const expiresInDays = Number.parseInt(createExpiryDays, 10);
    if (Number.isNaN(expiresInDays) || expiresInDays < 1 || expiresInDays > MAX_EXPIRY_DAYS) {
      setError(`Expiry must be between 1 and ${MAX_EXPIRY_DAYS} days`);
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const created = await createPersonalAccessToken({ name, expiresInDays });
      setCreateOpen(false);
      setCreateName("");
      setCreateExpiryDays(String(DEFAULT_EXPIRY_DAYS));
      setCreatedToken(created);
      setCopied(false);
      setSuccess("Personal access token created");
      loadTokens();
    } catch (err: unknown) {
      const message =
        err instanceof AuthRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to create personal access token";
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (token: PersonalAccessToken) => {
    if (token.revoked) return;
    if (!window.confirm(`Revoke personal access token "${token.name}"?`)) return;

    setError(null);
    try {
      await revokePersonalAccessToken(token.id);
      setSuccess(`Revoked token "${token.name}"`);
      loadTokens();
    } catch (err: unknown) {
      const message =
        err instanceof AuthRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to revoke personal access token";
      setError(message);
    }
  };

  const handleCopy = async () => {
    if (!createdToken?.token) return;
    try {
      await navigator.clipboard.writeText(createdToken.token);
      setCopied(true);
    } catch {
      setError("Unable to copy token to clipboard");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6">Personal access tokens</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Use tokens for CMS API and MCP access. Pass as{" "}
            <Box component="code" sx={{ fontSize: "0.85em" }}>
              Authorization: Bearer pat-…
            </Box>{" "}
            or{" "}
            <Box component="code" sx={{ fontSize: "0.85em" }}>
              x-pat-id
            </Box>
            .
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
          Create token
        </Button>
      </Box>

      <Paper sx={{ ...tablePaperSx, p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          MCP connection
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          SAMI CMS MCP is embedded in the CMS API (Streamable HTTP). Use your personal access
          token in the Authorization header. For Cursor IDE, save the JSON below as{" "}
          <Box component="code" sx={{ fontSize: "0.85em" }}>
            .cursor/mcp.json
          </Box>{" "}
          in your project.
        </Typography>
        <CopyableBlock label="MCP endpoint" value={mcpEndpointUrl} />
        <CopyableBlock label="Cursor mcp.json" value={mcpConfigJson} multiline />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={tablePaperSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Token</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No personal access tokens yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tokens.map((token) => {
                  const status = tokenStatus(token);
                  return (
                    <TableRow key={token.id} hover>
                      <TableCell>{token.name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" component="code">
                          {token.tokenPrefix}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={status.label} color={status.color} />
                      </TableCell>
                      <TableCell>{formatDate(token.expiresAt)}</TableCell>
                      <TableCell>{formatDate(token.lastUsedAt)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={token.revoked ? "Already revoked" : "Revoke token"}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={token.revoked}
                              onClick={() => handleRevoke(token)}
                              aria-label={`Revoke ${token.name}`}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create personal access token</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Token name"
            placeholder="e.g. MCP integration"
            fullWidth
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            disabled={creating}
          />
          <TextField
            margin="dense"
            label="Expires in (days)"
            type="number"
            fullWidth
            value={createExpiryDays}
            onChange={(e) => setCreateExpiryDays(e.target.value)}
            helperText={`Default ${DEFAULT_EXPIRY_DAYS} days, max ${MAX_EXPIRY_DAYS} days`}
            inputProps={{ min: 1, max: MAX_EXPIRY_DAYS }}
            disabled={creating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createdToken != null}
        onClose={() => setCreatedToken(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Token created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this token now. You will not be able to see it again.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {createdToken?.name} · expires {formatDate(createdToken?.expiresAt)}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "action.hover",
              fontFamily: "monospace",
              fontSize: "0.85rem",
              wordBreak: "break-all",
            }}
          >
            <Box sx={{ flex: 1 }}>{createdToken?.token}</Box>
            <Tooltip title={copied ? "Copied" : "Copy token"}>
              <IconButton size="small" onClick={handleCopy} aria-label="Copy token">
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {createdToken?.token ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                MCP connection (Cursor)
              </Typography>
              <CopyableBlock
                label="MCP endpoint"
                value={mcpEndpointUrl}
              />
              <CopyableBlock
                label="Cursor mcp.json"
                value={buildCursorMcpJson(createdToken.token)}
                multiline
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedToken(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PersonalAccessTokensPanel;
